import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Mail } from 'lucide-react';
import styled from 'styled-components';

const ContactFormArea = styled.textarea`
  &::-webkit-scrollbar { width: 0px; }
  overflow-y: auto;
  max-height: 460px;
`;

const FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const baseInput =
  'w-full rounded-xl px-4 py-3 bg-white/[0.04] border text-white placeholder-[#a99bd0] transition-colors focus:outline-none focus:ring-2 focus:ring-violet-bright/50';

const fieldClass = (hasError) =>
  `${baseInput} ${hasError ? 'border-rose-400 ring-2 ring-rose-400/50' : 'border-white/40 focus:border-violet-bright'}`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactForm = ({ isModalOpen, setIsModalOpen }) => {
  const [prenom, setPrenom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);

  const validate = () => {
    const e = {};
    if (!prenom.trim()) e.prenom = 'Veuillez indiquer votre prénom.';
    if (!name.trim()) e.nom = 'Veuillez indiquer votre nom.';
    if (!email.trim()) e.email = 'Veuillez indiquer votre adresse email.';
    else if (!EMAIL_RE.test(email)) e.email = 'Le format de l’adresse email est invalide.';
    if (!message.trim()) e.message = 'Veuillez saisir votre message.';
    return e;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);

    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const firstId = e.prenom ? 'prenom' : e.nom ? 'nom' : e.email ? 'email' : 'message';
      document.getElementById(firstId)?.focus();
      return;
    }

    try {
      const response = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: name, prenom, email, message }),
      });

      if (response.ok) {
        setStatus('success');
        setPrenom('');
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      triggerRef.current?.focus();
      return;
    }

    triggerRef.current = document.activeElement;
    setStatus(null);
    setErrors({});
    dialogRef.current?.focus();

    const handleKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        setIsModalOpen(false);
        return;
      }
      if (ev.key === 'Tab') {
        const focusable = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTORS) ?? []);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (ev.shiftKey) {
          if (document.activeElement === first) {
            ev.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, setIsModalOpen]);

  if (!isModalOpen) return null;

  const errProps = (key) =>
    errors[key]
      ? { 'aria-invalid': true, 'aria-describedby': `${key}-error` }
      : { 'aria-invalid': undefined };

  const ErrorMsg = ({ id }) =>
    errors[id] ? (
      <p id={`${id}-error`} role="alert" className="text-rose-300 text-sm mt-1">
        {errors[id]}
      </p>
    ) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Formulaire de contact"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8 bg-[#0a0218]/80 backdrop-blur-md overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsModalOpen(false);
      }}
    >
      <form
        ref={dialogRef}
        tabIndex={-1}
        onSubmit={handleSubmit}
        noValidate
        className="relative w-full max-w-2xl card-surface ring-gradient rounded-3xl p-6 sm:p-9 my-auto shadow-glow outline-none"
      >
        <button
          type="button"
          onClick={() => setIsModalOpen(false)}
          aria-label="Fermer le formulaire de contact"
          className="absolute top-4 right-4 grid place-items-center h-10 w-10 rounded-full glass text-white hover:bg-white/10 transition"
        >
          <X size={20} />
        </button>

        <span className="eyebrow">Parlons de votre projet</span>
        <h2 className="mt-3 font-display font-extrabold text-3xl">
          Contactez-<span className="text-gradient">nous</span>
        </h2>
        <p className="mt-2 text-sm text-[#a99bd0]">
          Les champs suivis d'un astérisque (<span className="text-rose-300">*</span>) sont obligatoires.
        </p>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="prenom" className="text-sm text-[#cbbef0]">
              Prénom <span className="text-rose-300" aria-hidden="true">*</span>
            </label>
            <input id="prenom" type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)}
              placeholder="Votre prénom" autoComplete="given-name" required aria-required="true"
              className={fieldClass(errors.prenom)} {...errProps('prenom')} />
            <ErrorMsg id="prenom" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="nom" className="text-sm text-[#cbbef0]">
              Nom <span className="text-rose-300" aria-hidden="true">*</span>
            </label>
            <input id="nom" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Votre nom" autoComplete="family-name" required aria-required="true"
              className={fieldClass(errors.nom)} {...errProps('nom')} />
            <ErrorMsg id="nom" />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="email" className="text-sm text-[#cbbef0]">
              Email <span className="text-rose-300" aria-hidden="true">*</span>
            </label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com" autoComplete="email" required aria-required="true"
              className={fieldClass(errors.email)} {...errProps('email')} />
            <ErrorMsg id="email" />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="message" className="text-sm text-[#cbbef0]">
              Message <span className="text-rose-300" aria-hidden="true">*</span>
            </label>
            <ContactFormArea id="message" value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Décrivez votre besoin…" required aria-required="true"
              className={`${fieldClass(errors.message)} h-36 resize-none`} {...errProps('message')} />
            <ErrorMsg id="message" />
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
          <button type="submit" className="btn btn-primary w-full sm:w-auto group">
            Envoyer le message
            <Send size={18} className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <a href="mailto:contact@placedesvictoires.tech"
            className="inline-flex items-center gap-2 text-sm text-[#bcaee6] hover:text-white transition-colors underline-animation">
            <Mail size={16} /> contact@placedesvictoires.tech
          </a>
        </div>

        {status === 'success' && (
          <p role="status" className="mt-4 text-center text-emerald-300">Votre message a bien été envoyé ! Merci.</p>
        )}
        {status === 'error' && (
          <p role="alert" className="mt-4 text-center text-rose-300">Une erreur est survenue, veuillez réessayer.</p>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
